'use client';

import { ComponentPropsWithoutRef } from 'react';
import { useAutoplayInView } from '@/hooks/use-autoplay-in-view';
import { cn } from '@/lib/utils';

interface MarqueeProps extends ComponentPropsWithoutRef<'div'> {
  /** Reverse direction. */
  reverse?: boolean;
  /** Pause animation on hover. */
  pauseOnHover?: boolean;
  /** Vertical instead of horizontal. */
  vertical?: boolean;
  /** Number of repeats. */
  repeat?: number;
}

/**
 * Magic UI–style infinite marquee. Children are duplicated `repeat` times.
 * Animation speed is controlled via `--duration` CSS var on the parent.
 */
export function Marquee({
  className,
  reverse = false,
  pauseOnHover = false,
  children,
  vertical = false,
  repeat = 4,
  ...props
}: MarqueeProps) {
  const { ref, isInView } = useAutoplayInView();

  return (
    <div
      ref={ref}
      {...props}
      className={cn(
        'group flex overflow-hidden p-2 [--duration:40s] [--gap:1rem] gap-(--gap)',
        {
          'flex-row': !vertical,
          'flex-col': vertical,
        },
        className,
      )}
    >
      {Array(repeat)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className={cn('flex shrink-0 justify-around gap-(--gap)', {
              'animate-[marquee_var(--duration)_linear_infinite] flex-row': !vertical,
              'animate-[marquee-vertical_var(--duration)_linear_infinite] flex-col': vertical,
              'group-hover:paused': pauseOnHover,
              'direction-[reverse]': reverse,
              paused: !isInView,
            })}
          >
            {children}
          </div>
        ))}
    </div>
  );
}
